import { useMemo } from "react";
import {
  isNextStep,
  nextStep,
  resolveSteps,
  setupProgress,
  type OnboardingStepKey,
  type OnboardingStepState,
} from "@marketing/shared";
import { useBrands, type Brand } from "@/lib/queries";

// =============================================================================
// What the owner should do next.
//
// A social media manager has a real order of operations: there is nothing to
// write for until a brand exists, nowhere to publish until a page is
// connected, and no voice to write in until someone sets one. Showing every
// destination at once on day one is how a product gets abandoned at the second
// screen.
//
// So the sidebar shows every destination but marks exactly one as next, and
// greys the ones whose prerequisite is unmet. Nothing is hidden and nothing is
// blocked — a returning user who knows where they are going is never stopped,
// a first-time user is never left guessing, and once setup is finished the
// guidance disappears entirely rather than decorating the whole menu.
//
// The sequence itself lives in @marketing/shared so it can be tested directly;
// this hook only answers "which steps are done" from data already fetched.
//
// Completion is derived from counts the brands list already returns, not from
// a "has_onboarded" flag. A flag goes stale the moment someone deletes their
// only page; counts cannot.
// =============================================================================

export type StepKey = OnboardingStepKey;
export type { OnboardingStepState };

/** Where each step is done. */
export const STEP_ROUTES: Record<OnboardingStepKey, string> = {
  brand: "/brands",
  connect: "/connect",
  voice: "/voice",
  post: "/photo",
};

export type Onboarding = {
  steps: OnboardingStepState[];
  /** The first unfinished step, or null once setup is complete. */
  next: OnboardingStepState | null;
  doneCount: number;
  total: number;
  complete: boolean;
  /** Still fetching — callers should not render a "do this first" prompt yet. */
  loading: boolean;
};

/**
 * Voice counts as done once either half of the Brand voice screen is saved.
 * One Save writes both records, but a brand that filled in only its facts has
 * still done the step.
 */
function completedFrom(brands: Brand[]): Record<OnboardingStepKey, boolean> {
  return {
    brand: brands.length > 0,
    connect: brands.some((b) => (b._count?.socialAccounts ?? 0) > 0),
    voice: brands.some((b) => (b._count?.voices ?? 0) > 0 || Boolean(b.profile)),
    post: brands.some((b) => (b._count?.posts ?? 0) > 0),
  };
}

export function useOnboarding(): Onboarding {
  const brands = useBrands();

  return useMemo(() => {
    const list = brands.data ?? [];
    const loading = brands.isLoading;

    const steps = resolveSteps(completedFrom(list));
    const progress = setupProgress(steps);

    return {
      steps,
      // While loading, every count reads as zero, which would point a
      // returning user at "Add your first brand". Suppress until it is known.
      next: loading ? null : nextStep(steps),
      doneCount: progress.done,
      total: progress.total,
      complete: !loading && progress.complete,
      loading,
    };
  }, [brands.data, brands.isLoading]);
}

export { isNextStep };
